import React, { useMemo, useRef } from 'react';
import type { DataSourceEventPayload } from '@splunk/dashboard-types';
import EnterprisePopoverContentFactory from '../EnterprisePopoverContentFactory';
import PopoverStatusIcon from '../PopoverStatusIcon';
import { usePopoverState } from '../hooks/usePopoverState';
import { getIconStatus } from '../utils/getIconStatus';
import getStatusMessage from '../utils/getStatusMessage';

const STATUS_ICON_HEIGHT = 20;
const STATUS_ICON_SIZE = 18;

export interface ComponentStatusIconProps {
    dataSource?: DataSourceEventPayload;
    componentId: string;
    type?: 'input' | 'visualization';
    validationMessage?: string;
    warningMessage?: string;
}

export const ComponentStatusIcon = ({
    dataSource,
    componentId,
    type,
    validationMessage,
    warningMessage,
}: ComponentStatusIconProps): JSX.Element | null => {
    const anchor = useRef<HTMLElement | null>(null);
    const { open, onRequestOpen, onRequestClose } = usePopoverState({
        stopPropagation: true,
        anchor,
    });

    const iconStatus = useMemo(() => {
        if (type === 'input') {
            if (validationMessage) {
                return 'error';
            }
            if (warningMessage) {
                return 'warning';
            }
        }

        return getIconStatus(dataSource);
    }, [type, warningMessage, validationMessage, dataSource]);

    if (!iconStatus) {
        return null;
    }

    const statusMessage = getStatusMessage(dataSource);
    const hasContent =
        !!validationMessage || !!warningMessage || !!statusMessage;

    const message = validationMessage?.trim()
        ? validationMessage
        : warningMessage;

    return (
        <PopoverStatusIcon
            height={STATUS_ICON_HEIGHT}
            iconSize={STATUS_ICON_SIZE}
            iconSizeOnHover={STATUS_ICON_SIZE}
            open={open}
            onRequestOpen={onRequestOpen}
            onRequestClose={onRequestClose}
            status={iconStatus}
            anchor={anchor}
            hasContent={hasContent}
        >
            <EnterprisePopoverContentFactory
                dataSource={dataSource}
                componentId={componentId}
                onRequestClose={onRequestClose}
                message={type === 'input' ? message : undefined}
                type={type}
            />
        </PopoverStatusIcon>
    );
};
